import useMediaQuery from '@/utils/useMediaQuery';
import { FormRow } from '../formComponents/helpers';
import { DatePicker } from '../formComponents';
import React, { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { CLOSED_DATES_CONTENT_QUERYResult } from 'sanity.types';
import { ClosedDateItem } from '../formComponents/DatePicker/DatePicker';

interface QuoteFormDateTimeInputsProps {
    setCollectionDate: Dispatch<SetStateAction<Date | null>>;
    setReturnDate: Dispatch<SetStateAction<Date | null>>;
    setReturnDatePriceCalculator: Dispatch<
        SetStateAction<Date | string | number>
    >;
    closedDates: CLOSED_DATES_CONTENT_QUERYResult;
}

const QuoteFormDateTimeInputs = ({
    setCollectionDate,
    setReturnDate,
    setReturnDatePriceCalculator,
    closedDates,
}: QuoteFormDateTimeInputsProps) => {
    const isBreakpoint = useMediaQuery(970);
    const [currentReturnDate, setCurrentReturnDate] = useState<Date | null>(
        null
    );

    // Effect to sync return date changes
    useEffect(() => {
        if (currentReturnDate) {
            setReturnDatePriceCalculator(currentReturnDate);
        }
    }, [currentReturnDate, setReturnDatePriceCalculator]);

    const handleCollectionDateChange = (date: Date | null) => {
        setCollectionDate(date);
        if (date && currentReturnDate) {
            setReturnDatePriceCalculator(currentReturnDate);
        }
    };

    const handleReturnDateChange = (date: Date | null) => {
        setReturnDate(date);
        setCurrentReturnDate(date);
        if (date) {
            setReturnDatePriceCalculator(date);
        }
    };

    const sanitizedClosedDates = React.useMemo(
        () =>
            (closedDates ?? []).filter(
                (cd): cd is ClosedDateItem => typeof cd?.date === 'string'
            ),
        [closedDates]
    );

    // Compute reopenDates mapping from closedDates if present
    const reopenDates: Record<string, string> = {};
    sanitizedClosedDates.forEach((cd) => {
        if (cd.date && cd.reopenDate) {
            reopenDates[cd.date] = cd.reopenDate;
        }
    });

    const collectionPickerProps = {
        name: 'collectionDate',
        onChangeHandler: handleCollectionDateChange,
        excludeSundays: true as const,
        closedDays: sanitizedClosedDates,
        showError: true,
    };

    const returnPickerProps = {
        name: 'returnDate',
        onChangeHandler: handleReturnDateChange,
        excludeSundays: false as const,
        closedDays: sanitizedClosedDates,
        showError: true,
    };

    return (
        <div>
            {!isBreakpoint ? (
                <FormRow
                    label="Pick-up date & time:"
                    content={
                        <DatePicker
                            {...collectionPickerProps}
                            reopenDates={reopenDates}
                        />
                    }
                />
            ) : (
                <FormRow
                    label="Pick-up date & time:"
                    content={
                        <DatePicker
                            {...collectionPickerProps}
                            reopenDates={reopenDates}
                        />
                    }
                />
            )}

            {!isBreakpoint ? (
                <FormRow
                    label="Drop-off date & time:"
                    content={
                        <>
                            <DatePicker {...returnPickerProps} />
                        </>
                    }
                />
            ) : (
                <FormRow
                    label="Drop-off date & time:"
                    content={
                        <>
                            <DatePicker {...returnPickerProps} />
                        </>
                    }
                />
            )}
        </div>
    );
};

export default QuoteFormDateTimeInputs;
